import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    Grid,
    IconButton,
    LinearProgress,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    Snackbar,
    Alert,
} from '@mui/material';
import React, { useState } from 'react';
import { bytesToMB, getProgressPercent } from '../../utils/helper';
import api from '../../utilities/api';
import { useUserSubscriptions } from '../../hooks/useSubscriptions';
import { useUserTransactions } from '../../hooks/useTransactions';
import dayjs from 'dayjs';
import CheckIcon from '@mui/icons-material/Check';
import HistoryIcon from '@mui/icons-material/History';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoIcon from '@mui/icons-material/Info';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Tab, Tabs } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const UserProfileModal = ({ open, onClose, user, initialTab }) => {
    const theme = useTheme(); // Get the current theme
    const navigate = useNavigate();

    // Map tab names to indices
    const tabMap = {
        'details': 0,
        'subscriptions': 1,
        'transactions': 2
    };

    // State for handling the action menu
    const [actionAnchorEl, setActionAnchorEl] = useState(null);
    const [currentSubscriptionId, setCurrentSubscriptionId] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    // Update tab value when modal opens or initialTab changes
    React.useEffect(() => {
        if (open && initialTab && tabMap[initialTab] !== undefined) {
            setTabValue(tabMap[initialTab]);
        } else if (open) {
            setTabValue(0); // Reset to details by default when opening
        }
    }, [open, initialTab]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Fetch user subscriptions using the hook
    const { data: subscriptionResponse, isLoading: loadingSubscriptions } = useUserSubscriptions(
        open ? user?._id : null
    );
    
    // Fetch user transactions
    const { data: transactionResponse, isLoading: loadingTransactions } = useUserTransactions(
        open ? user?._id : null
    );
    
    const userSubscriptions = subscriptionResponse?.data || [];
    const userTransactions = transactionResponse?.data || [];

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Handle opening the action menu
    const handleActionMenuOpen = (event, subscriptionId) => {
        setActionAnchorEl(event.currentTarget);
        setCurrentSubscriptionId(subscriptionId);
    };

    // Handle closing the action menu
    const handleActionMenuClose = () => {
        setActionAnchorEl(null);
        setCurrentSubscriptionId(null);
    };

    // Handle selecting an action from the menu
    const handleActionSelect = (action) => {
        // Here you would implement the action functionality
        console.log(`Action ${action} selected for subscription ${currentSubscriptionId}`);
        handleActionMenuClose();
    };

    if (!user) return null;

    const getIconImage = (type) => {
        const baseUrl = 'https://cdn.clikkle.com/images/';
        switch (type?.toLowerCase()) {
            case 'file':
            case 'files':
                return `${baseUrl}files/logo/2025/clikkle%20files.png`;
            case 'esign':
                return `${baseUrl}e-sign/logo/2025/clikkle%20esign.png`;
            case 'projects':
                return `${baseUrl}projects/logo/2025/clikkle%20projects.png`;
            case 'app':
            case 'worksuite':
                return `${baseUrl}worksuite/2025/clikkle%20worksuite.png`;
            case 'campaigns':
                return `${baseUrl}campaigns/logo/2025/clikkle%20campaigns.png`;
            case 'chat':
                return `${baseUrl}chat/logo/2025/clikkle%20chat.png`;
            case 'hr':
                return `${baseUrl}hr/logo/2025/clikkle%20hr.png`;
            default:
                return `${baseUrl}worksuite/2025/clikkle%20worksuite.png`;
        }
    };

    const getProductName = (type) => {
        switch (type?.toLowerCase()) {
            case 'file':
            case 'files': return 'Clikkle Files';
            case 'esign': return 'Clikkle eSign';
            case 'projects': return 'Clikkle Projects';
            case 'app':
            case 'worksuite': return 'Clikkle Worksuite';
            case 'campaigns': return 'Clikkle Campaigns';
            case 'chat': return 'Clikkle Chat';
            case 'hr': return 'Clikkle Hr';
            default: return type || 'Clikkle Service';
        }
    };

    // Account actions
    const accountActions = [
        { name: 'Suspend', color: 'warning' },
        { name: 'Delete', color: 'error' },
        { name: 'Send Verification Email', color: 'primary' }
    ];

    // helper to safely format dates
    const fmtDate = (d) => {
        try {
            if (!d) return '-';
            const date = new Date(d);
            if (isNaN(date)) return String(d);
            return date.toLocaleString();
        } catch (e) {
            return String(d);
        }
    };

    async function deleteUser() {
    try {
        if (!user?._id) {
        console.error("No user id provided");
        return;
        }
        console.log("Deleting user with id:", user._id);
        const response = await api.delete(`/api/user/users/${user._id}`);
        console.log("User deleted successfully:", response.data);
        setSnackbar({
            open: true,
            message: 'User deleted successfully',
            severity: 'success'
        });
        setTimeout(() => {
            window.location.reload()
            onClose()
        }, 1500);
    } catch (error) {
        console.error("Error deleting user:", error.response?.data || error.message);
        setSnackbar({
            open: true,
            message: error.response?.data?.message || 'Failed to delete user',
            severity: 'error'
        });
    }
    }

    async function sendVerificationEmail() {
        try {
            if (!user?._id) {
                console.error("No user id provided");
                return;
            }
            console.log("Sending verification email to user with id:", user._id);
            const response = await api.post(`/api/user/users/${user._id}/send-verification-email`);
            console.log("Verification email sent successfully:", response.data);
            setSnackbar({
                open: true,
                message: 'Verification email sent successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error("Error sending verification email:", error.response?.data || error.message);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to send verification email',
                severity: 'error'
            });
        }
    }

    async function suspendUser() {
        try {
            if (!user?._id) {
                console.error("No user id provided");
                return;
            }
            console.log("Suspended user with id:", user._id);
            const response = await api.patch(`/api/user/users/${user._id}/suspend`);
            console.log("User Suspended successfully:", response.data);
            setSnackbar({
                open: true,
                message: 'User suspended successfully',
                severity: 'success'
            });
            setTimeout(() => {
                onClose()
            }, 1500);
        } catch (error) {
            console.error("Error Suspended user:", error.response?.data || error.message);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to suspend user',
                severity: 'error'
            });
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    bgcolor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    borderRadius: 3,
                    boxShadow: 24,
                    p: { xs: 2, sm: 3 },
                    maxHeight: '90vh',
                }
            }}
        >
            <Box sx={{ position: 'relative', overflowY: 'auto' }}>
                <Typography variant="h4" fontWeight="bold">
                    User Profile
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.text.primary,
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {/* User Header Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                    {user.profilePicture ? (
                        <Box
                            component="img"
                            src={user.profilePicture}
                            alt="Profile"
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                objectFit: 'cover',
                            }}
                        />
                    ) : (
                        <Avatar
                            sx={{
                                width: 40,
                                height: 40,
                                bgcolor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                            }}
                        >
                            {(user?.firstName?.[0] || '').toUpperCase()}
                            {(user?.lastName?.[0] || '').toUpperCase()}
                        </Avatar>
                    )}
                    <Box sx={{ ml: { xs: 0, sm: 2 }, textAlign: { xs: 'center', sm: 'left' }, mt: { xs: 1, sm: 0 } }}>
                        <Typography variant="h5" fontWeight="bold">{user.firstName} {user.lastName}</Typography>
                        <Chip
                            label={ user.isVerified === true ? 'Verified' : "Unverified"}
                            size="small"
                            sx={{
                                bgcolor: user.isVerified === true ? theme.palette.success.main : theme.palette.grey[500],
                                color: theme.palette.common.white,
                                '& .MuiChip-label': { px: 1.5 }
                            }}
                        />
                    </Box>
                </Box>

                {/* User Stats */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: { xs: "20px", sm: "40px", md: "80px" }, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center', minWidth: { xs: '25%', sm: 'auto' } }}>
                        <Typography variant="h6">{fmtDate(user?.createdAt)}</Typography>
                        <Typography variant="body2" color="text.secondary">Registration Date</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', minWidth: { xs: '25%', sm: 'auto' } }}>
                        <Typography variant="h6">{userSubscriptions.filter(s => s.isActive).length}</Typography>
                        <Typography variant="body2" color="text.secondary">Subscription</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', minWidth: { xs: '25%', sm: 'auto' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Typography variant="h6" color="warning.main">
                                {bytesToMB(user?.storage?.usedSpace)} / {user?.storagePermission?.noOfAllowed || '0'} {(user?.storagePermission?.format || 'MB')}
                            </Typography>
                            <IconButton 
                                size="small" 
                                sx={{ color: 'warning.main', p: 0 }}
                                onClick={() => {
                                    onClose();
                                    navigate(`/storage/list?userId=${user._id || user.id}`);
                                }}
                                title="View User Files"
                            >
                                <ArrowForwardIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                        <Typography variant="body2" color="text.secondary">Storage</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', minWidth: { xs: '25%', sm: 'auto' } }}>
                        <Typography variant="h6" color="primary.main">
                            ${userTransactions.reduce((acc, tx) => acc + Number(tx.amount || 0), 0).toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Spent</Typography>
                    </Box>
                </Box>

                <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mt: 4 }}>
                    <Grid item xs={12}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                            <Tabs value={tabValue} onChange={handleTabChange} aria-label="user profile tabs">
                                <Tab icon={<InfoIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Details" sx={{ minHeight: 48 }} />
                                <Tab icon={<PaymentIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Subscriptions" sx={{ minHeight: 48 }} />
                                <Tab icon={<HistoryIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Transactions" sx={{ minHeight: 48 }} />
                            </Tabs>
                        </Box>
                    </Grid>

                    {tabValue === 0 && (
                        <Grid item xs={12} sm={10} md={10}>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>User Details</Typography>
                            <Grid container spacing={{ xs: 1, sm: 2 }}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Email:</Typography>
                                    <Typography variant="body1" color="primary" sx={{ wordBreak: 'break-word' }}>
                                        {user.email}
                                    </Typography>
                                </Grid>

                                {/* existing fields preserved */}
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Phone Number:</Typography>
                                    <Typography variant="body1"> {user?.phoneNumber || 'Not Provided'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">IP Address:</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body1">
                                            {user?.registerIpAddress
                                                ? user.registerIpAddress === '::1'
                                                    ? 'DEVELOPMENT_MODE'
                                                    : user.registerIpAddress.replace(/^::ffff:/, '')
                                                : 'Not Provided'}
                                        </Typography>
                                        {user?.registerIpAddress && user.registerIpAddress !== '::1' && (
                                            <IconButton 
                                                size="small" 
                                                sx={{ color: 'primary.main', p: 0 }}
                                                onClick={() => {
                                                    onClose();
                                                    navigate(`/location/ip-address/all?search=${user.registerIpAddress.replace(/^::ffff:/, '')}`);
                                                }}
                                                title="View IP Details"
                                            >
                                                <ArrowForwardIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Date of Birth:</Typography>
                                    <Typography variant="body1">{user?.dob || 'Not Provided'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Recovery Email:</Typography>
                                    <Typography variant="body1">{user?.recoveryEmail || 'Not Provided'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Session Active:</Typography>
                                    <Typography variant="body1">{user?.session ? 'Yes' : 'No'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Account Type:</Typography>
                                    <Typography variant="body1">{user?.accountType || 'Not Provided'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Role:</Typography>
                                    <Typography variant="body1">{user?.role || 'Not Provided'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Referred By:</Typography>
                                    <Typography variant="body1">{user?.referred_by || 'Not Provided'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Firebase UID:</Typography>
                                    <Typography variant="body1">{user?.firebaseUid || 'Not Provided'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Auth Providers:</Typography>
                                    <Typography variant="body1">{Array.isArray(user?.authProvider) ? user.authProvider.join(', ') : (user?.authProvider || 'Auth')}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Provider IDs:</Typography>
                                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>{user?.providerIds ? JSON.stringify(user.providerIds) : 'Not Provided'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Username:</Typography>
                                    <Typography variant="body1">{user?.username || 'Not Provided'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Gender:</Typography>
                                    <Typography variant="body1">{user?.gender || 'Not Provided'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Created At:</Typography>
                                    <Typography variant="body1">{fmtDate(user?.createdAt)}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Updated At:</Typography>
                                    <Typography variant="body1">{fmtDate(user?.updatedAt)}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Is Verified:</Typography>
                                    <Typography variant="body1">{user?.isVerified ? 'Yes' : 'No'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Profile Picture (URL):</Typography>
                                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>{user?.profilePicture || '-'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Reset Password Token:</Typography>
                                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>{user?.resetPasswordToken ? `${String(user.resetPasswordToken).slice(0, 20)}...` : '-'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="body2" color="text.secondary">Reset Password Expire:</Typography>
                                    <Typography variant="body1">{fmtDate(user?.resetPasswordExpire)}</Typography>
                                </Grid>

                                {/* Business details expanded */}
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">Business Details:</Typography>
                                    {user?.business ? (
                                        <Box sx={{ mt: 0.5, pl: 1 }}>
                                            <Typography variant="body2">Business Name: {user.business.businessName || 'Not Provided'}</Typography>
                                            <Typography variant="body2">Domain: {user.business.domain || 'Not Provided'}</Typography>
                                            <Typography variant="body2">Employees: {user.business.numberOfEmployees || 'Not Provided'}</Typography>
                                            <Typography variant="body2">Phone: {user.business.businessPhoneNumber || 'Not Provided'}</Typography>
                                            <Typography variant="body2">Street: {user.business.streetAddress || 'Not Provided'}</Typography>
                                            <Typography variant="body2">Street Line 2: {user.business.streetAddressLine2 || 'Not Provided'}</Typography>
                                            <Typography variant="body2">City: {user.business.city || 'Not Provided'}</Typography>
                                            <Typography variant="body2">LGA: {user.business.LocalGovernmentArea || 'Not Provided'}</Typography>
                                            <Typography variant="body2">Postal Code: {user.business.postalCode || 'Not Provided'}</Typography>
                                            <Typography variant="body2">State: {user.business.state || 'Not Provided'}</Typography>
                                        </Box>
                                    ) : (
                                        <Typography variant="body1">Not Provided</Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {tabValue === 1 && (
                        <Grid item xs={12}>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Subscriptions</Typography>
                            <Box sx={{ overflow: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Product</TableCell>
                                            <TableCell>Plan & Billing</TableCell>
                                            <TableCell>Start Date</TableCell>
                                            <TableCell>Next Billing</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loadingSubscriptions ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                                    <Typography variant="body2" color="text.secondary">Loading subscriptions...</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : userSubscriptions.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                                    <Typography variant="body2" color="text.secondary">No subscriptions found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            userSubscriptions.map((subscription) => (
                                                <TableRow key={subscription._id}>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Box
                                                                component="img"
                                                                src={getIconImage(subscription.type)}
                                                                alt={getProductName(subscription.type)}
                                                                sx={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    mr: 1,
                                                                    objectFit: 'contain'
                                                                }}
                                                            />
                                                            <Typography variant="body2">{getProductName(subscription.type)}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {subscription.planName || 'Starter'}
                                                                {subscription.amount ? ` ($${Number(subscription.amount).toFixed(2)})` : ''}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {subscription.interval ? `${subscription.interval.charAt(0).toUpperCase() + subscription.interval.slice(1)}` : 'One-time'}
                                                                {subscription.orgName && ` • ${subscription.orgName}`}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem' }}>
                                                        {subscription.startDate ? dayjs(subscription.startDate).format('MMM D, YYYY') : '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem' }}>
                                                        {subscription.isActive && subscription.startDate && subscription.interval !== 'one-time' ? (
                                                            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
                                                                {dayjs(subscription.startDate).add(1, subscription.interval || 'month').format('MMM D, YYYY')}
                                                            </Typography>
                                                        ) : subscription.cancelDate ? (
                                                            <Typography variant="caption" color="error.main">
                                                                Cancelled on {dayjs(subscription.cancelDate).format('MMM D, YYYY')}
                                                            </Typography>
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={
                                                                !subscription.isActive ? 'Cancelled' : 
                                                                parseFloat(subscription.amount || 0) === 0 ? 'Free Active' : 
                                                                'Paid Active'
                                                            }
                                                            size="small"
                                                            sx={{
                                                                bgcolor: !subscription.isActive ? theme.palette.error.main : (parseFloat(subscription.amount || 0) === 0 ? theme.palette.info.main : theme.palette.success.main),
                                                                color: theme.palette.common.white,
                                                                fontSize: '0.7rem',
                                                                height: 24
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>

                            {/* Action Menu */}
                            <Menu
                                anchorEl={actionAnchorEl}
                                open={Boolean(actionAnchorEl)}
                                onClose={handleActionMenuClose}
                                PaperProps={{
                                    sx: {
                                        bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[800],
                                        color: theme.palette.common.white,
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                        width: '150px'
                                    }
                                }}
                            >
                                <MenuItem
                                    onClick={() => handleActionSelect('Activate')}
                                    sx={{
                                        py: 1,
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                border: `2px solid ${theme.palette.success.main}`,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    bgcolor: theme.palette.success.main,
                                                    borderRadius: '50%'
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="body2">Activate</Typography>
                                    </Box>
                                </MenuItem>
                                <MenuItem
                                    onClick={() => handleActionSelect('Deactivate')}
                                    sx={{
                                        py: 1,
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                border: `2px solid ${theme.palette.error.main}`,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    bgcolor: theme.palette.error.main,
                                                    borderRadius: '50%'
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="body2">Deactivate</Typography>
                                    </Box>
                                </MenuItem>
                                <MenuItem
                                    onClick={() => handleActionSelect('Overdue')}
                                    sx={{
                                        py: 1,
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                border: `2px solid ${theme.palette.warning.main}`,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    bgcolor: theme.palette.warning.main,
                                                    borderRadius: '50%'
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="body2">Overdue</Typography>
                                    </Box>
                                </MenuItem>
                            </Menu>
                        </Grid>
                    )}

                    {tabValue === 2 && (
                        <Grid item xs={12}>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Transaction History</Typography>
                            <Box sx={{ overflow: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Product</TableCell>
                                            <TableCell>Amount</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loadingTransactions ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                                    <Typography variant="body2" color="text.secondary">Loading transactions...</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : userTransactions.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                                    <Typography variant="body2" color="text.secondary">No transactions found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            userTransactions.map((tx) => (
                                                <TableRow key={tx._id}>
                                                    <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                                        {tx.transactionId || tx._id.slice(-8)}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem' }}>
                                                        {dayjs(tx.paymentDate || tx.createdAt).format('MMM D, YYYY HH:mm')}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                            {getProductName(tx.productName || tx.type)}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {tx.planName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                                        ${Number(tx.amount).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={tx.status || 'Completed'}
                                                            size="small"
                                                            color={tx.status === 'failed' ? 'error' : tx.status === 'refunded' ? 'warning' : 'success'}
                                                            sx={{ fontSize: '0.65rem', height: 20 }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Grid>
                    )}

                    {/* Account Action */}
                    <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Account Action</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {accountActions.map((action) => (
                                    <Button
                                    key={action.name}
                                    variant="contained"
                                    color={action.color}
                                    size="small"
                                    onClick={
                                        action.name === 'Delete' ? deleteUser : 
                                        action.name === 'Suspend' ? suspendUser : 
                                        action.name === 'Send Verification Email' ? sendVerificationEmail : 
                                        null
                                    }
                                    disabled={
                                        (action.name === 'Send Verification Email' && user.isVerified === true) ||
                                        (action.name === 'Suspend' && user.isVerified === false)
                                    }
                                    sx={{
                                        borderRadius: 1,
                                        minWidth: { xs: 80, sm: 100 },
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}
                                >
                                    {action.name}
                                </Button>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default UserProfileModal;
